import { z } from 'zod';

/**
 * Schema for key-value pairs (headers, params)
 * Stored as Record<string, string | number> in the API
 */
const keyValueRecordSchema = z
  .record(z.string(), z.union([z.string(), z.number()]))
  .nullable()
  .optional();

/**
 * Schema for JSON body objects
 * Can be any valid JSON object
 */
const jsonBodySchema = z.record(z.string(), z.unknown()).nullable().optional();

/**
 * Key-value pairs the sync has to send as strings. The API validates nothing
 * inside these, so the input side is `unknown`: whatever a task has stored has
 * to open, including shapes that are not records at all.
 *
 * On save, numbers and booleans become strings (creating a task rejects
 * non-string header values), nulls stay (a null extract path means the whole
 * response), an emptied JSON field becomes an empty record, and anything else
 * passes through verbatim.
 */
const stringValueRecordCodec = z.codec(
  z.unknown().optional(),
  z.unknown().optional(),
  {
    decode: (record) => {
      if (record === '') {
        return {};
      }

      if (
        record == null ||
        typeof record !== 'object' ||
        Array.isArray(record)
      ) {
        return record;
      }

      return Object.fromEntries(
        Object.entries(record).map(([key, value]) => [
          key,
          typeof value === 'number' || typeof value === 'boolean'
            ? String(value)
            : value,
        ]),
      );
    },
    encode: (record) => record,
  },
);

const AUTH_STEP_METHODS = ['GET', 'POST'] as const;

/**
 * The sync lowercases the method, runs a missing one as POST, and abandons the
 * whole authentication on anything but GET or POST
 * (`data_sync_connector.py::_run_multi_step_auth`).
 *
 * A method it cannot run is therefore shown as stored rather than quietly
 * rewritten, and saving the step reports it instead: the step is broken today
 * and the user, not the form, decides what it should be.
 */
const authStepMethodCodec = z.codec(
  z.string(),
  z.string().nullable().optional(),
  {
    decode: (method, ctx) => {
      const normalized = method.toUpperCase();

      if (!AUTH_STEP_METHODS.some((allowed) => allowed === normalized)) {
        ctx.issues.push({
          code: 'invalid_value',
          input: method,
          values: [...AUTH_STEP_METHODS],
        });
      }

      return normalized;
    },
    encode: (stored) => {
      const method = String(stored ?? '');
      const normalized = method.toUpperCase();

      if (method === '') return 'POST';

      return AUTH_STEP_METHODS.some((allowed) => allowed === normalized)
        ? normalized
        : method;
    },
  },
);

/**
 * One step of a multi-step authentication flow. The API stores the steps as a
 * plain list of objects and validates nothing inside them, so this schema must
 * not be stricter either: an endpoint may be a placeholder instead of a URL,
 * fields may be missing or null, and unknown keys have to survive an edit.
 */
const authStepFieldsSchema = z.looseObject({
  body: z
    .unknown()
    .optional()
    .describe('Request body for this step, sent on POST only'),
  endpoint: z
    .string()
    .nullable()
    .optional()
    .describe('Endpoint called in this step, placeholders allowed'),
  extract: stringValueRecordCodec.describe(
    'Values to keep from the response, as name to dot-separated path',
  ),
  headers: stringValueRecordCodec.describe('Headers to send in this step'),
  method: authStepMethodCodec.describe('HTTP method to use for this step'),
  name: z
    .string()
    .nullable()
    .optional()
    .describe('Name of the step, used to identify it in the sync logs'),
  params: stringValueRecordCodec.describe(
    'Query parameters to send in this step',
  ),
});

/**
 * The same step as the API stores it, after the field codecs above have run:
 * methods and record values are already normalized, nothing else is assumed.
 */
const storedAuthStepSchema = z.looseObject({
  body: z.unknown().optional(),
  endpoint: z.string().nullable().optional(),
  extract: z.unknown().optional(),
  headers: z.unknown().optional(),
  method: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  params: z.unknown().optional(),
});

/**
 * The lenient field schema above, with two save-time rules on top. Only saving
 * (`decode`) applies them, so any stored step still opens:
 *
 * - A step needs an endpoint, raised as the issue `z.string().min(1)` produces
 *   so the shared translated "required" message applies.
 * - A GET step is saved without its body: the sync sends a body on POST only
 *   and the form hides it for GET, so keeping it would leave credentials in
 *   the stored task that neither the sync nor the form ever shows again. An
 *   emptied body is dropped the same way.
 */
export const authStepSchema = z.codec(
  authStepFieldsSchema,
  storedAuthStepSchema,
  {
    decode: (step, ctx) => {
      if (!step.endpoint) {
        ctx.issues.push({
          code: 'too_small',
          inclusive: true,
          input: step.endpoint,
          minimum: 1,
          origin: 'string',
          path: ['endpoint'],
        });
      }

      if (step.method === 'GET' || step.body === '') {
        const { body: _dropped, ...withoutBody } = step;
        return withoutBody;
      }

      return step;
    },
    // A field codec only runs on a present key, and an empty method becomes POST.
    encode: (stored) => ({ ...stored, method: stored.method ?? '' }),
  },
);

/**
 * JSON API connection schema for REST API sync
 */
export const jsonApiConnectionSchema = z.object({
  // Main API configuration
  apiBody: jsonBodySchema.describe('JSON body to include in the request'),
  apiEndpoint: z.url().describe('Endpoint URL to query the data from'),
  apiHeaders: keyValueRecordSchema.describe(
    'Headers to include in the request',
  ),

  // Delta sync configuration
  apiLastFetchedBodyPath: z
    .string()
    .nullable()
    .optional()
    .describe(
      'Dot-separated path in the request body for the last fetched value',
    ),
  apiLastFetchedFormat: z
    .string()
    .nullable()
    .optional()
    .describe(
      'Format string for the last fetched value used in incremental sync',
    ),
  apiLastFetchedParam: z
    .string()
    .nullable()
    .optional()
    .describe(
      'Query parameter for the last fetched value for incremental sync',
    ),

  // Pagination configuration
  apiMaxPageSize: z.coerce
    .number()
    .positive()
    .nullable()
    .optional()
    .describe('Maximum number of items to fetch per API page'),
  apiMethod: z
    .enum(['GET', 'POST'])
    .default('GET')
    .describe('HTTP method to use'),
  apiOffsetBodyPath: z
    .string()
    .nullable()
    .optional()
    .describe('Dot-separated path in the request body to send offset to API'),
  apiOffsetParam: z
    .string()
    .nullable()
    .optional()
    .describe('Query parameter to send offset to API'),
  apiPageSizeBodyPath: z
    .string()
    .nullable()
    .optional()
    .describe(
      'Dot-separated path in the request body to send page size to API',
    ),
  apiParams: keyValueRecordSchema.describe(
    'Query parameters to include in the request',
  ),
  apiResponsePathToItems: z
    .string()
    .nullable()
    .optional()
    .describe(
      'Dot-separated path within the API response where the relevant items are located',
    ),
  apiUsePagination: z
    .boolean()
    .default(false)
    .describe('Whether to use pagination when fetching data'),

  // Authentication configuration
  authApiBody: jsonBodySchema.describe(
    'Request body to send when authenticating against the API',
  ),
  authApiHeaders: keyValueRecordSchema.describe(
    'Headers to include in authentication API requests',
  ),
  authApiMethod: z
    .enum(['GET', 'POST'])
    .nullable()
    .optional()
    .describe('HTTP method to use for authentication requests'),
  authApiParams: keyValueRecordSchema.describe(
    'Query parameters for authentication API requests',
  ),
  authCookieName: z
    .string()
    .nullable()
    .optional()
    .describe('Cookie where auth token is stored'),
  authCustomHeaders: stringValueRecordCodec.describe(
    'Headers to add to every data request, with {placeholder} values taken from the auth steps',
  ),
  authRequestEndpoint: z
    .url()
    .nullable()
    .optional()
    .describe('Endpoint for obtaining auth tokens for the API'),
  authSteps: z
    .array(authStepSchema)
    .nullable()
    .optional()
    .describe('Multi-step authentication flow, run in order before each sync'),
  authTokenPath: z
    .string()
    .nullable()
    .optional()
    .describe(
      'Dot-separated path within the authentication response where the token is located',
    ),

  connectorType: z.literal('custom_json_api'),
});
